<template>
    <div class="component-field-display-edit">
        <div v-if="readonly">
            {{ value || '-' }}
        </div>
        <div v-else>
            <div v-if="showInput">
                <b-form-input
                    ref="input"
                    type="text"
                    :value="inputValue"
                    @input="onInput"
                    @blur="onBlur"
                    @keyup.enter="onEnter"
                />
            </div>
            <button
                v-else
                type="button"
                class="field-link"
                :class="{ 'field-link-muted': !value || !String(value).trim() }"
                @click="openEditor"
            >
                {{ displayText }}
            </button>
        </div>
    </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'

export default {
    name: 'ComponentFieldDisplayEdit',
    components: {
        BFormInput,
    },
    props: {
        value: {
            type: String,
            default: '',
        },
        emptyPlaceholder: {
            type: String,
            default: '',
        },
        emptyDisplaysLink: {
            type: Boolean,
            default: false,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showInput: this.autoFocus || (!this.value && !this.emptyDisplaysLink),
            inputValue: this.value,
            inputDebounceTimer: null,
            hasAutoFocused: false,
        }
    },
    computed: {
        displayText() {
            if (this.value && String(this.value).trim()) {
                return this.value
            }
            return this.emptyPlaceholder || '-'
        },
    },
    watch: {
        value(next) {
            this.inputValue = next
            if (!next && !this.emptyDisplaysLink && !this.showInput) {
                this.showInput = true
            }
        },
        autoFocus(next) {
            if (next) {
                this.triggerAutoFocus()
            }
        },
    },
    methods: {
        onInput(next) {
            this.inputValue = next
            if (this.inputDebounceTimer) {
                clearTimeout(this.inputDebounceTimer)
            }
            this.inputDebounceTimer = setTimeout(() => {
                this.$emit('input', this.inputValue)
                this.inputDebounceTimer = null
            }, 2000)
        },
        flushPendingInput() {
            if (!this.inputDebounceTimer) {
                return
            }
            clearTimeout(this.inputDebounceTimer)
            this.inputDebounceTimer = null
            this.$emit('input', this.inputValue)
        },
        onBlur() {
            this.flushPendingInput()
            const hasValue = this.inputValue && String(this.inputValue).trim()
            if (hasValue || this.emptyDisplaysLink) {
                this.showInput = false
            }
        },
        onEnter() {
            this.flushPendingInput()
            const hasValue = this.inputValue && String(this.inputValue).trim()
            if (hasValue || this.emptyDisplaysLink) {
                this.showInput = false
            }
        },
        openEditor() {
            this.showInput = true
            this.inputValue = this.value
            this.focusInput()
        },
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.input && this.$refs.input.focus) {
                    this.$refs.input.focus()
                }
            })
        },
        triggerAutoFocus() {
            if (this.hasAutoFocused || this.readonly) {
                return
            }
            this.showInput = true
            this.hasAutoFocused = true
            this.focusInput()
            this.$emit('autofocus-done')
        },
    },
    beforeDestroy() {
        if (this.inputDebounceTimer) {
            clearTimeout(this.inputDebounceTimer)
            this.inputDebounceTimer = null
        }
    },
    mounted() {
        if (this.autoFocus) {
            this.triggerAutoFocus()
        }
    },
}
</script>

<style scoped>
.field-link {
    background: transparent;
    border: 0;
    padding: 0;
    color: #2b6cb0;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
}
.field-link:hover {
    color: #1e4e8c;
}
.field-link-muted {
    color: #6c757d;
    text-decoration-color: #adb5bd;
}
.field-link-muted:hover {
    color: #6c757d;
    text-decoration-color: #6c757d;
}
</style>
